/** @file Dopesheet tab: row headers beside the Canvas2D key area for the current animation. */
import { useMemo, useState } from 'react';
import { useStore } from 'zustand';
import type { EditorDeps } from '../../playback/editorDeps';
import { currentDoc } from '../../stores/currentDoc';
import { docStore } from '../../stores/docStore';
import { playheadStore } from '../../stores/playheadStore';
import { selectionStore } from '../../stores/selectionStore';
import { useT } from '../../shell/i18n/useT';
import { useElementHeight } from '../../ui-kit/useElementHeight';
import { dopesheetModel, type DopeModel } from './model/dopesheetModel';
import { RowHeaders } from './RowHeaders';
import { useDopesheetCanvas } from './useDopesheetCanvas';

function DopesheetBody({ model, deps, onToggle }: { model: DopeModel; deps: EditorDeps; onToggle: (target: string) => void }) {
  const t = useT();
  const canvas = useDopesheetCanvas(model, deps);
  const height = useElementHeight(canvas.ref, 200);
  return (
    <div className="timeline-dopesheet">
      <RowHeaders rows={model.layout.rows} scrollY={canvas.scrollY} height={height} t={t} onToggle={onToggle} onScroll={canvas.scroll} />
      <canvas ref={canvas.ref} className="timeline-canvas" tabIndex={0} aria-label={t('panels.timeline.keys')} {...canvas.handlers} />
    </div>
  );
}

/** A copy of `set` with `target` added, or removed when it was there. */
function toggled(set: ReadonlySet<string>, target: string): ReadonlySet<string> {
  const next = new Set(set);
  if (!next.delete(target)) next.add(target);
  return next;
}

/** The dopesheet of the playhead's animation; selected bones and slots get rows even without keys. */
export function DopesheetView({ deps }: { deps: EditorDeps }) {
  const doc = useStore(docStore, currentDoc);
  const animation = useStore(playheadStore, (s) => s.animation);
  const bones = useStore(selectionStore, (s) => s.bones);
  const slots = useStore(selectionStore, (s) => s.slots);
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const model = useMemo(
    () => (doc === null || animation === null ? null : dopesheetModel(doc, animation, new Set([...bones, ...slots]), collapsed)),
    [doc, animation, bones, slots, collapsed],
  );
  const toggle = (target: string): void => { setCollapsed((c) => toggled(c, target)); };
  return model === null ? null : <DopesheetBody model={model} deps={deps} onToggle={toggle} />;
}
